import { makeAutoObservable } from 'mobx'
import { differenceInHours, differenceInMinutes, differenceInSeconds } from 'date-fns'
import set from 'lodash.set'

import { EventType, IBaby, IEvent, Nullable } from 'types'
import { analyticsContext } from 'contexts'
import { notification } from 'services'
import { EventApi } from 'api'
import { isISOValid } from 'utils'

import { createEvent, serializeEvent } from './utils'

export default class EventStore {
  id
  type
  baby: Nullable<IBaby> = null
  event: Nullable<IEvent> = null
  originalEvent: Nullable<Record<string, any>>
  loading = true

  constructor(id: string, type?: EventType) {
    this.id = id
    this.type = type

    makeAutoObservable(this, undefined, { autoBind: true })
  }

  get isNew() {
    return this.id === 'new'
  }

  isDurationValid = () => {
    if (!this.event) {
      return true
    }

    if (!this.event?.startTime || !this.event?.endTime) {
      return false
    }

    const diffInMins = differenceInMinutes(this.event?.endTime, this.event?.startTime)
    const diffInHours = differenceInHours(this.event?.endTime, this.event?.startTime)
    const diffInSeconds = differenceInSeconds(this.event?.endTime, this.event?.startTime)

    return diffInMins >= 0 && diffInHours >= 0 && diffInSeconds >= 0
  }

  get validFunctions(): Record<EventType, () => boolean> {
    return {
      [EventType.BREAST_LEFT]: () => this.isDurationValid(),
      [EventType.BREAST_RIGHT]: () => this.isDurationValid(),
      [EventType.BOTTLE]: () => this.isDurationValid(),
      [EventType.SLEEP_DAY]: () => this.isDurationValid(),
      [EventType.SLEEP_NIGHT]: () => this.isDurationValid(),
      [EventType.POO]: () => isISOValid(this.event?.startTime || ''),
      [EventType.PEE]: () => isISOValid(this.event?.startTime || ''),
    }
  }

  get isValid() {
    const validationFunction = this.validFunctions[this.type as EventType]

    return validationFunction?.()
  }

  setType = (type: EventType) => {
    this.type = type
  }

  setBaby = (baby: IBaby) => {
    this.baby = baby
  }

  setEventField = (field: string, value: any) => {
    if (this.event) {
      set(this.event, field, value)
    }
  }

  init = (event: IEvent) => {
    if (this.isNew) {
      this.initEmptyEvent()
    } else {
      this.type = event?.eventTypeCode
      this.event = event
      this.originalEvent = event
    }
    this.loading = false
  }

  initEmptyEvent() {
    const event = createEvent(this.type)
    this.event = event
    this.originalEvent = event
  }

  *save() {
    if (!this.baby) {
      return
    }

    this.loading = true

    try {
      const serialized = serializeEvent(this.event as IEvent, this.type as EventType)

      if (this.isNew) {
        yield EventApi.createEvent(this.baby?.id, serialized)
      } else {
        yield EventApi.editEvent(this.baby?.id, this.id, serialized)
        analyticsContext.trackEvent('event_saved', { event_name: serialized.eventTypeCode })
      }

      notification.success(this.isNew ? 'Событие создано' : 'Событие сохранено')
      return true
    } catch {
      notification.error('Ошибка создания события')
      return false
    } finally {
      this.loading = false
    }
  }

  *saveCurrent() {
    if (!this.baby) {
      return
    }

    this.loading = true

    const event = {
      ...this.event,
      eventTypeCode: this.type,
      endTime: null,
    }

    try {
      yield EventApi.editEvent(this.baby?.id, this.id, event as IEvent)
      analyticsContext.trackEvent('event_saved', { event_name: event.eventTypeCode })

      notification.success('Событие сохранено')
      return true
    } catch {
      notification.error('Ошибка создания события')
      return false
    } finally {
      this.loading = false
    }
  }

  *remove() {
    if (this.id && this.baby?.id) {
      this.loading = true
      try {
        yield EventApi.deleteEvent(this.baby?.id, this.id)
        notification.success('Событие удалено')
        return true
      } catch {
        notification.error('Ошибка удаления события')
        return false
      } finally {
        this.loading = false
      }
    }
  }
}
